import { CoreError, assertSafeInt } from './errors'
import type { BuildContext, DocLineInput } from './documents'
import type { Paisa } from './money'
import type { Milli } from './qty'
import type {
  KitchenStation,
  KotItemRow,
  KotTicketRow,
  OrderItemRow,
  OrderRequestItemRow,
  OrderRequestRow,
  OrderRow,
  ServiceType,
  SyncOp,
  SyncRowBase,
} from './rows'
import { computeDocument, type DocumentTotals, type RoundOffMode } from './totals'

// Available in browsers, Workers and Node 19+; declared here so core needs no DOM/Node types.
declare const crypto: { randomUUID(): string }

// Open orders (restaurant mode). An order is an open bill on a table, or a
// takeaway or delivery order with no table and a short number for the day; its
// items are append-only, so cancelling one is a new row with a negative
// quantity pointing at the original. Checkout turns the net items into the
// lines of a normal invoice (buildInvoice) and closes the order in the same tx.

function base(ctx: BuildContext, id?: string): SyncRowBase {
  return {
    id: id ?? (ctx.newId ?? (() => crypto.randomUUID()))(),
    tenant_id: ctx.tenantId,
    created_at: ctx.now,
    updated_at: ctx.now,
    deleted_at: null,
  }
}

function op(table: string, row: object): SyncOp {
  return { table, op: 'upsert', row: { ...row } }
}

const isOpen = (order: Pick<OrderRow, 'status'>) =>
  order.status === 'open' || order.status === 'billed'

function assertOpen(order: Pick<OrderRow, 'status'>): void {
  if (!isOpen(order)) throw new CoreError('order_closed', `this order is already ${order.status}`)
}

/** An ordered item with what is left of it after cancellations. */
export interface NetOrderItem {
  item: OrderItemRow
  qtyMilli: Milli
}

/**
 * The items still on the order, oldest first: each ordered row less the rows
 * that cancel it. Never below zero (two devices may cancel the same item offline).
 */
export function netOrderItems(items: readonly OrderItemRow[]): NetOrderItem[] {
  const cancelled = new Map<string, number>()
  for (const i of items) {
    if (i.deleted_at || !i.void_of_id) continue
    cancelled.set(i.void_of_id, (cancelled.get(i.void_of_id) ?? 0) - i.qty_milli)
  }
  return items
    .filter((i) => !i.deleted_at && !i.void_of_id && i.qty_milli > 0)
    .map((item) => ({
      item,
      qtyMilli: Math.max(0, item.qty_milli - (cancelled.get(item.id) ?? 0)),
    }))
    .filter((n) => n.qtyMilli > 0)
    .sort((a, b) => a.item.created_at.localeCompare(b.item.created_at))
}

/**
 * The order as bill lines: the same product at the same price and tax becomes
 * one line ("2× momo" then "1× momo" is billed as 3). Custom items stay apart.
 */
export function orderLines(items: readonly OrderItemRow[]): DocLineInput[] {
  const lines: DocLineInput[] = []
  const byKey = new Map<string, DocLineInput>()
  for (const { item, qtyMilli } of netOrderItems(items)) {
    const key = item.product_id
      ? [
          item.product_id,
          item.unit_price_paisa,
          item.price_includes_tax,
          item.tax_rate_bp,
          item.tax_rate_id ?? '',
          item.description,
        ].join('|')
      : null
    const same = key ? byKey.get(key) : undefined
    if (same) {
      same.qtyMilli += qtyMilli
      continue
    }
    const line: DocLineInput = {
      productId: item.product_id,
      description: item.description,
      qtyMilli,
      unitPricePaisa: item.unit_price_paisa,
      priceIncludesTax: item.price_includes_tax,
      taxRateId: item.tax_rate_id,
      taxRateBp: item.tax_rate_bp,
    }
    if (key) byKey.set(key, line)
    lines.push(line)
  }
  return lines
}

const NO_TOTALS: DocumentTotals = {
  subtotalPaisa: 0,
  discountPaisa: 0,
  taxablePaisa: 0,
  taxPaisa: 0,
  roundOffPaisa: 0,
  totalPaisa: 0,
}

/** What the order comes to so far (the same arithmetic as its invoice). */
export function orderTotals(
  items: readonly OrderItemRow[],
  roundOff: RoundOffMode = 'nearest_rupee',
): DocumentTotals {
  const lines = orderLines(items)
  return lines.length === 0 ? NO_TOTALS : computeDocument(lines, { roundOff }).totals
}

export interface OpenOrderInput {
  id?: string
  tableId: string | null
  covers?: number
  customerId?: string | null
  notes?: string | null
  /** Default dine_in. Takeaway and delivery need orderNo and no table. */
  serviceType?: ServiceType
  /** The device's next order number for the day (takeaway and delivery). */
  orderNo?: number | null
  /** The name called out at pickup. */
  label?: string | null
  /** Who looks after it (the person opening it). The server fills in the pusher when null. */
  serverId?: string | null
}

export function openOrder(
  ctx: BuildContext,
  input: OpenOrderInput,
): { order: OrderRow; ops: SyncOp[] } {
  const covers = input.covers ?? 1
  if (!Number.isInteger(covers) || covers <= 0)
    throw new CoreError('invalid_input', 'guests must be a whole number above zero')
  const serviceType = input.serviceType ?? 'dine_in'
  const orderNo = input.orderNo ?? null
  if (serviceType !== 'dine_in') {
    if (input.tableId !== null)
      throw new CoreError('invalid_input', `a ${serviceType} order has no table`)
    if (orderNo === null || !Number.isInteger(orderNo) || orderNo <= 0)
      throw new CoreError('invalid_input', 'the order number must be a whole number above zero')
  }
  const label = input.label?.trim() || null
  if (label !== null && label.length > 60)
    throw new CoreError('invalid_input', 'the name is too long (60 characters at most)')
  const order: OrderRow = {
    ...base(ctx, input.id),
    table_id: input.tableId,
    status: 'open',
    covers,
    customer_id: input.customerId ?? null,
    notes: input.notes?.trim() || null,
    invoice_id: null,
    merged_into_id: null,
    opened_at: ctx.now,
    closed_at: null,
    void_reason: null,
    service_type: serviceType,
    order_no: serviceType === 'dine_in' ? null : orderNo,
    label,
    server_id: input.serverId ?? null,
  }
  return { order, ops: [op('orders', order)] }
}

export interface OrderItemInput {
  productId?: string | null
  description: string
  qtyMilli: Milli
  unitPricePaisa: Paisa
  priceIncludesTax: boolean
  taxRateId?: string | null
  taxRateBp: number
  note?: string | null
  /** The product's kitchen station (default: the kitchen). */
  station?: KitchenStation
}

function itemRow(ctx: BuildContext, orderId: string, input: OrderItemInput): OrderItemRow {
  assertSafeInt(input.qtyMilli, 'qtyMilli')
  assertSafeInt(input.unitPricePaisa, 'unitPricePaisa')
  if (input.qtyMilli <= 0) throw new CoreError('invalid_line', 'quantity must be positive')
  if (input.unitPricePaisa < 0) throw new CoreError('invalid_line', 'price cannot be negative')
  if (!input.description.trim()) throw new CoreError('invalid_line', 'an item needs a name')
  return {
    ...base(ctx),
    order_id: orderId,
    product_id: input.productId ?? null,
    description: input.description.trim(),
    qty_milli: input.qtyMilli,
    unit_price_paisa: input.unitPricePaisa,
    price_includes_tax: input.priceIncludesTax,
    tax_rate_id: input.taxRateId ?? null,
    tax_rate_bp: input.taxRateBp,
    note: input.note?.trim() || null,
    void_of_id: null,
    void_reason: null,
    station: input.station ?? 'kitchen',
  }
}

export function addOrderItem(
  ctx: BuildContext,
  order: Pick<OrderRow, 'id' | 'status'>,
  input: OrderItemInput,
): { item: OrderItemRow; ops: SyncOp[] } {
  assertOpen(order)
  const item = itemRow(ctx, order.id, input)
  return { item, ops: [op('order_items', item)] }
}

/**
 * Cancels some or all of an item: a new row with a negative quantity.
 * `items` are the order's items, to know how much of it is left.
 */
export function voidOrderItem(
  ctx: BuildContext,
  order: Pick<OrderRow, 'id' | 'status'>,
  items: readonly OrderItemRow[],
  itemId: string,
  qtyMilli: Milli,
  reason: string,
): { item: OrderItemRow; ops: SyncOp[] } {
  assertOpen(order)
  const left = netOrderItems(items).find((n) => n.item.id === itemId)
  if (!left || left.item.order_id !== order.id)
    throw new CoreError('invalid_input', 'this item is no longer on the order')
  assertSafeInt(qtyMilli, 'qtyMilli')
  if (qtyMilli <= 0 || qtyMilli > left.qtyMilli)
    throw new CoreError('invalid_input', 'cancel between one and what is left of the item')
  if (!reason.trim()) throw new CoreError('invalid_input', 'say why the item is cancelled')
  const item: OrderItemRow = {
    ...left.item,
    ...base(ctx),
    qty_milli: -qtyMilli,
    note: null,
    void_of_id: itemId,
    void_reason: reason.trim(),
  }
  return { item, ops: [op('order_items', item)] }
}

/** Moves the order to another table (or off any table with null). */
export function moveOrder(
  ctx: BuildContext,
  order: OrderRow,
  tableId: string | null,
): { order: OrderRow; ops: SyncOp[] } {
  assertOpen(order)
  if (order.service_type !== 'dine_in' && tableId !== null)
    throw new CoreError('invalid_input', `a ${order.service_type} order has no table`)
  const moved: OrderRow = { ...order, table_id: tableId, updated_at: ctx.now }
  return { order: moved, ops: [op('orders', moved)] }
}

/**
 * Gives an open order to another server (a waiter takes it for themselves; only a
 * manager gives one to someone else, which the server checks).
 */
export function setOrderServer(
  ctx: BuildContext,
  order: OrderRow,
  serverId: string,
): { order: OrderRow; ops: SyncOp[] } {
  assertOpen(order)
  const updated: OrderRow = { ...order, server_id: serverId, updated_at: ctx.now }
  return { order: updated, ops: [op('orders', updated)] }
}

/** Changes guests, customer, notes or the pickup name of an open order. */
export function updateOrder(
  ctx: BuildContext,
  order: OrderRow,
  changes: Partial<Pick<OrderRow, 'covers' | 'customer_id' | 'notes' | 'label'>>,
): { order: OrderRow; ops: SyncOp[] } {
  assertOpen(order)
  const updated: OrderRow = { ...order, ...changes, updated_at: ctx.now }
  if (!Number.isInteger(updated.covers) || updated.covers <= 0)
    throw new CoreError('invalid_input', 'guests must be a whole number above zero')
  return { order: updated, ops: [op('orders', updated)] }
}

/** The ids of the item rows a kitchen ticket already covers. */
function sentIds(sent: readonly Pick<KotItemRow, 'order_item_id' | 'deleted_at'>[]): Set<string> {
  return new Set(sent.filter((l) => !l.deleted_at).map((l) => l.order_item_id))
}

/**
 * Merges `source` into `target` in one tx: what is left on the source is added
 * to the target as new items, and the source is void with merged_into_id set.
 * `sent`: the source's ticket links. An item the kitchen already has moves
 * without a station, so it is not sent again.
 */
export function mergeOrders(
  ctx: BuildContext,
  source: OrderRow,
  sourceItems: readonly OrderItemRow[],
  target: OrderRow,
  reason = 'Merged',
  sent: readonly Pick<KotItemRow, 'order_item_id' | 'deleted_at'>[] = [],
): { source: OrderRow; target: OrderRow; items: OrderItemRow[]; ops: SyncOp[] } {
  assertOpen(source)
  assertOpen(target)
  if (source.id === target.id)
    throw new CoreError('invalid_input', 'an order cannot merge into itself')
  if (source.service_type !== target.service_type)
    throw new CoreError('invalid_input', 'orders merge only with orders of the same type')
  const told = sentIds(sent)
  const items = netOrderItems(sourceItems).map(({ item, qtyMilli }) =>
    itemRow(ctx, target.id, {
      productId: item.product_id,
      description: item.description,
      qtyMilli,
      unitPricePaisa: item.unit_price_paisa,
      priceIncludesTax: item.price_includes_tax,
      taxRateId: item.tax_rate_id,
      taxRateBp: item.tax_rate_bp,
      note: item.note,
      station: told.has(item.id) ? 'none' : item.station,
    }),
  )
  const merged: OrderRow = {
    ...source,
    status: 'void',
    merged_into_id: target.id,
    void_reason: reason,
    closed_at: ctx.now,
    updated_at: ctx.now,
  }
  const kept: OrderRow = {
    ...target,
    covers: target.covers + source.covers,
    updated_at: ctx.now,
  }
  return {
    source: merged,
    target: kept,
    items,
    ops: [op('orders', merged), op('orders', kept), ...items.map((i) => op('order_items', i))],
  }
}

/**
 * Splits a bill by item: the picked quantities move to a new order on the same
 * table, in one tx (a cancelling row on the source, a new row on the new
 * order). Each order is then checked out into its own invoice. An item the
 * kitchen already has moves without a station on both rows: nothing is
 * cancelled or sent again.
 */
export function splitOrder(
  ctx: BuildContext,
  source: OrderRow,
  sourceItems: readonly OrderItemRow[],
  sent: readonly Pick<KotItemRow, 'order_item_id' | 'deleted_at'>[],
  picks: readonly { itemId: string; qtyMilli: Milli }[],
  covers = 1,
): { order: OrderRow; items: OrderItemRow[]; ops: SyncOp[] } {
  assertOpen(source)
  const left = new Map(netOrderItems(sourceItems).map((n) => [n.item.id, n]))
  const chosen = picks.filter((p) => p.qtyMilli !== 0)
  if (chosen.length === 0)
    throw new CoreError('invalid_input', 'choose what goes on the other bill')
  const total = [...left.values()].reduce((sum, n) => sum + n.qtyMilli, 0)
  if (chosen.reduce((sum, p) => sum + p.qtyMilli, 0) >= total)
    throw new CoreError('invalid_input', 'leave at least one item on this bill')
  const { order, ops } = openOrder(ctx, {
    tableId: source.table_id,
    covers,
    serviceType: source.service_type,
    orderNo: source.order_no,
    label: source.label,
    // The other bill stays with the same server.
    serverId: source.server_id,
  })
  const told = sentIds(sent)
  const items: OrderItemRow[] = []
  for (const pick of chosen) {
    const from = left.get(pick.itemId)
    assertSafeInt(pick.qtyMilli, 'qtyMilli')
    if (!from || pick.qtyMilli < 0 || pick.qtyMilli > from.qtyMilli)
      throw new CoreError('invalid_input', 'move between one and what is left of the item')
    const station = told.has(from.item.id) ? 'none' : from.item.station
    const out: OrderItemRow = {
      ...from.item,
      ...base(ctx),
      qty_milli: -pick.qtyMilli,
      note: null,
      void_of_id: from.item.id,
      void_reason: 'Moved to another bill',
      station,
    }
    const moved = itemRow(ctx, order.id, {
      productId: from.item.product_id,
      description: from.item.description,
      qtyMilli: pick.qtyMilli,
      unitPricePaisa: from.item.unit_price_paisa,
      priceIncludesTax: from.item.price_includes_tax,
      taxRateId: from.item.tax_rate_id,
      taxRateBp: from.item.tax_rate_bp,
      note: from.item.note,
      station,
    })
    items.push(moved)
    ops.push(op('order_items', out), op('order_items', moved))
  }
  return { order, items, ops }
}

/**
 * Puts a note on an item ("less spicy"). Items never change, so the item is
 * cancelled and ordered again with the note, in one tx. Only for an item the
 * kitchen has not been sent: after that the kitchen is told in person.
 */
export function noteOrderItem(
  ctx: BuildContext,
  order: Pick<OrderRow, 'id' | 'status'>,
  items: readonly OrderItemRow[],
  itemId: string,
  note: string | null,
): { item: OrderItemRow; ops: SyncOp[] } {
  assertOpen(order)
  const left = netOrderItems(items).find((n) => n.item.id === itemId)
  if (!left || left.item.order_id !== order.id)
    throw new CoreError('invalid_input', 'this item is no longer on the order')
  const out: OrderItemRow = {
    ...left.item,
    ...base(ctx),
    qty_milli: -left.qtyMilli,
    note: null,
    void_of_id: itemId,
    void_reason: 'Note changed',
  }
  const item = itemRow(ctx, order.id, {
    productId: left.item.product_id,
    description: left.item.description,
    qtyMilli: left.qtyMilli,
    unitPricePaisa: left.item.unit_price_paisa,
    priceIncludesTax: left.item.price_includes_tax,
    taxRateId: left.item.tax_rate_id,
    taxRateBp: left.item.tax_rate_bp,
    note,
    station: left.item.station,
  })
  // The new row keeps the old one's place in the list.
  item.created_at = left.item.created_at
  return { item, ops: [op('order_items', out), op('order_items', item)] }
}

/** Voids an order (with items on it: managers only, the server checks). */
export function voidOrder(
  ctx: BuildContext,
  order: OrderRow,
  reason: string,
): { order: OrderRow; ops: SyncOp[] } {
  assertOpen(order)
  if (!reason.trim()) throw new CoreError('invalid_input', 'say why the order is cancelled')
  const voided: OrderRow = {
    ...order,
    status: 'void',
    void_reason: reason.trim(),
    closed_at: ctx.now,
    updated_at: ctx.now,
  }
  return { order: voided, ops: [op('orders', voided)] }
}

/** The op that closes an order; goes in the same tx as its finalized invoice. */
export function closeOrderOp(ctx: BuildContext, order: OrderRow, invoiceId: string): SyncOp {
  assertOpen(order)
  const closed: OrderRow = {
    ...order,
    status: 'closed',
    invoice_id: invoiceId,
    closed_at: ctx.now,
    updated_at: ctx.now,
  }
  return op('orders', closed)
}

/** The guest check was printed ('billed'), or an item was added after it ('open'). */
export function setOrderBilled(
  ctx: BuildContext,
  order: OrderRow,
  billed: boolean,
): { order: OrderRow; ops: SyncOp[] } {
  assertOpen(order)
  const next: OrderRow = { ...order, status: billed ? 'billed' : 'open', updated_at: ctx.now }
  return { order: next, ops: [op('orders', next)] }
}

// -----------------------------------------------------------------------------
// Kitchen tickets (KOT)
// -----------------------------------------------------------------------------

/** One line of a kitchen ticket. */
export interface KotLine {
  description: string
  qtyMilli: Milli
  note: string | null
  /** The kitchen already has this item: stop making it. */
  cancelled: boolean
}

/**
 * What a ticket tells the kitchen, from the order item rows it covers: an
 * item less its cancellations on the same ticket, and a cancel line for a
 * cancellation of an item sent earlier.
 */
export function kotLines(rows: readonly OrderItemRow[]): KotLine[] {
  const ids = new Set(rows.map((r) => r.id))
  const cancelledHere = new Map<string, number>()
  for (const r of rows) {
    if (r.void_of_id && ids.has(r.void_of_id))
      cancelledHere.set(r.void_of_id, (cancelledHere.get(r.void_of_id) ?? 0) - r.qty_milli)
  }
  // The same dish with the same note is one line ("2× Chicken momo").
  const lines: KotLine[] = []
  const push = (line: KotLine) => {
    const same = lines.find(
      (l) =>
        l.description === line.description &&
        l.note === line.note &&
        l.cancelled === line.cancelled,
    )
    if (same) same.qtyMilli += line.qtyMilli
    else lines.push(line)
  }
  for (const r of [...rows].sort((a, b) => a.created_at.localeCompare(b.created_at))) {
    if (r.void_of_id) {
      if (ids.has(r.void_of_id)) continue
      push({ description: r.description, qtyMilli: -r.qty_milli, note: null, cancelled: true })
    } else {
      const qtyMilli = r.qty_milli - (cancelledHere.get(r.id) ?? 0)
      if (qtyMilli > 0)
        push({ description: r.description, qtyMilli, note: r.note, cancelled: false })
    }
  }
  return lines
}

/**
 * The order item rows the kitchen has not been told about, by station. An
 * item cancelled before it was ever sent is left out with its cancellations:
 * the kitchen never needs to know.
 */
export function pendingKitchen(
  items: readonly OrderItemRow[],
  sent: readonly Pick<KotItemRow, 'order_item_id' | 'deleted_at'>[],
): Map<'kitchen' | 'bar', OrderItemRow[]> {
  const sentIds = new Set(sent.filter((l) => !l.deleted_at).map((l) => l.order_item_id))
  const unsent = items.filter((i) => !i.deleted_at && i.station !== 'none' && !sentIds.has(i.id))
  const out = new Map<'kitchen' | 'bar', OrderItemRow[]>()
  for (const station of ['kitchen', 'bar'] as const) {
    const rows = unsent.filter((i) => i.station === station)
    const lines = kotLines(rows)
    if (lines.length === 0) continue
    // Keep only rows that say something: drop an unsent item cancelled in full.
    const gone = new Set(
      rows
        .filter((r) => !r.void_of_id)
        .filter(
          (r) =>
            r.qty_milli +
              rows.filter((v) => v.void_of_id === r.id).reduce((sum, v) => sum + v.qty_milli, 0) <=
            0,
        )
        .map((r) => r.id),
    )
    out.set(
      station,
      rows.filter((r) => !gone.has(r.id) && !(r.void_of_id && gone.has(r.void_of_id))),
    )
  }
  return out
}

/** A ticket whose order was paid or cancelled leaves the Kitchen screens this long after. */
export const TICKET_CLEARS_MS = 60 * 60 * 1000

/**
 * Where a waiting ticket stands, worked out from its order and never stored: `live` (order
 * open), `paid` (food still owed), `stopped` (order cancelled) or `gone` (either, over an hour
 * ago). A merged order's ticket is judged by the order it went into, so never call this with
 * the void source of a merge.
 */
export function ticketStanding(
  order: Pick<OrderRow, 'status' | 'closed_at'> | null,
  nowMs: number,
): 'live' | 'paid' | 'stopped' | 'gone' {
  if (!order || order.status === 'open' || order.status === 'billed') return 'live'
  if (order.closed_at && Date.parse(order.closed_at) <= nowMs - TICKET_CLEARS_MS) return 'gone'
  return order.status === 'closed' ? 'paid' : 'stopped'
}

/**
 * "Send to kitchen": one ticket per station for what has not been sent, with
 * a link to every row it covers, in one tx. `nextNo` is the device's next
 * ticket number for the day. Nothing to send returns no tickets and no ops.
 */
export function sendToKitchen(
  ctx: BuildContext,
  order: Pick<OrderRow, 'id' | 'status'>,
  items: readonly OrderItemRow[],
  sent: readonly Pick<KotItemRow, 'order_item_id' | 'deleted_at'>[],
  nextNo: number,
): { tickets: { ticket: KotTicketRow; rows: OrderItemRow[] }[]; ops: SyncOp[] } {
  assertOpen(order)
  if (!Number.isInteger(nextNo) || nextNo <= 0)
    throw new CoreError('invalid_input', 'the ticket number must be a whole number above zero')
  const tickets: { ticket: KotTicketRow; rows: OrderItemRow[] }[] = []
  const ops: SyncOp[] = []
  for (const [station, rows] of pendingKitchen(items, sent)) {
    const ticket: KotTicketRow = {
      ...base(ctx),
      order_id: order.id,
      station,
      kot_no: nextNo + tickets.length,
      status: 'new',
      sent_at: ctx.now,
      ready_at: null,
      started_at: null,
    }
    tickets.push({ ticket, rows })
    ops.push(op('kot_tickets', ticket))
    for (const row of rows) {
      const link: KotItemRow = { ...base(ctx), kot_id: ticket.id, order_item_id: row.id }
      ops.push(op('kot_items', link))
    }
  }
  return { tickets, ops }
}

/**
 * The kitchen starts a ticket (`preparing`), marks it `ready`, or takes either
 * back. A ticket may go straight from new to ready.
 */
export function setKotStatus(
  ctx: BuildContext,
  ticket: KotTicketRow,
  status: KotTicketRow['status'],
): { ticket: KotTicketRow; ops: SyncOp[] } {
  const next: KotTicketRow = {
    ...ticket,
    status,
    started_at: status === 'new' ? null : (ticket.started_at ?? ctx.now),
    ready_at: status === 'ready' ? ctx.now : null,
    updated_at: ctx.now,
  }
  return { ticket: next, ops: [op('kot_tickets', next)] }
}

/**
 * Takes a paid or cancelled order's ticket off the Kitchen screens. It is done with but was
 * not made: a null `ready_at` means no Food ready alert and no "Made in" in the activity log.
 */
export function clearKotTicket(
  ctx: BuildContext,
  ticket: KotTicketRow,
): { ticket: KotTicketRow; ops: SyncOp[] } {
  const next: KotTicketRow = { ...ticket, status: 'ready', ready_at: null, updated_at: ctx.now }
  return { ticket: next, ops: [op('kot_tickets', next)] }
}

/**
 * Whether the kitchen has made everything sent for an order: it has tickets
 * and every one is ready. Never stored; a takeaway shows "Ready" from this.
 */
export function orderReady(
  tickets: readonly Pick<KotTicketRow, 'status' | 'deleted_at'>[],
): boolean {
  const live = tickets.filter((t) => !t.deleted_at)
  return live.length > 0 && live.every((t) => t.status === 'ready')
}

// -----------------------------------------------------------------------------
// Requests from guests (QR ordering)
// -----------------------------------------------------------------------------

/** How long a request sent to a free table waits for a waiter (the server's `_guest_free_wait`). */
export const GUEST_FREE_WAIT_MS = 15 * 60 * 1000

/** A request sent to a free table that nobody answered in time: no longer shown or counted. */
export function requestExpired(
  request: Pick<OrderRequestRow, 'status' | 'order_id' | 'created_at'>,
  nowMs: number,
): boolean {
  return (
    request.status === 'pending' &&
    request.order_id === null &&
    Date.parse(request.created_at) <= nowMs - GUEST_FREE_WAIT_MS
  )
}

/** What accepting needs to know of a requested product, as it is now. */
export interface RequestProduct {
  priceIncludesTax: boolean
  taxRateId: string | null
  taxRateBp: number
  station: KitchenStation
}

function decided(
  ctx: BuildContext,
  request: OrderRequestRow,
  status: 'accepted' | 'rejected',
): OrderRequestRow {
  if (request.status !== 'pending')
    throw new CoreError('request_decided', `this request was already ${request.status}`)
  return { ...request, status, decided_at: ctx.now, updated_at: ctx.now }
}

/**
 * Accepts a guest's order in one tx: the request is marked accepted and each
 * line joins the table's order as a new item (at the price the guest saw, with
 * the product's VAT and kitchen station as they are now). A billed order opens
 * again. A request sent to a free table has no order yet: pass the order just
 * opened for it (its ops go first in the same tx) and the request takes it. `products` has every requested product that still exists; a line
 * whose product is gone is left out.
 */
export function acceptRequest(
  ctx: BuildContext,
  order: OrderRow,
  request: OrderRequestRow,
  lines: readonly OrderRequestItemRow[],
  products: ReadonlyMap<string, RequestProduct>,
): { request: OrderRequestRow; items: OrderItemRow[]; ops: SyncOp[] } {
  assertOpen(order)
  const accepted = { ...decided(ctx, request, 'accepted'), order_id: request.order_id ?? order.id }
  const items: OrderItemRow[] = []
  for (const line of lines) {
    if (line.deleted_at || line.request_id !== request.id) continue
    const product = products.get(line.product_id)
    if (!product) continue
    items.push(
      itemRow(ctx, order.id, {
        productId: line.product_id,
        description: line.description,
        qtyMilli: line.qty_milli,
        unitPricePaisa: line.unit_price_paisa,
        priceIncludesTax: product.priceIncludesTax,
        taxRateId: product.taxRateId,
        taxRateBp: product.taxRateBp,
        note: line.note,
        station: product.station,
      }),
    )
  }
  const reopen = order.status === 'billed' ? setOrderBilled(ctx, order, false).ops : []
  return {
    request: accepted,
    items,
    ops: [...reopen, op('order_requests', accepted), ...items.map((i) => op('order_items', i))],
  }
}

/** Sends a request back, or marks a call for the waiter or the bill as done. */
export function decideRequest(
  ctx: BuildContext,
  request: OrderRequestRow,
  status: 'accepted' | 'rejected',
): { request: OrderRequestRow; ops: SyncOp[] } {
  const next = decided(ctx, request, status)
  return { request: next, ops: [op('order_requests', next)] }
}
